import React, { useState, useEffect, useContext } from 'react';
import { startOfWeek, addWeeks, addDays, format, isWithinInterval, parseISO, isFuture, isPast, isToday } from 'date-fns';
import { 
  Box, 
  Button, 
  Grid, 
  MenuItem, 
  Select, 
  Typography, 
  Dialog, 
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  Divider,
  Snackbar,
  Alert,
  Paper,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Fab
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import PayPeriodDetails from './PayPeriodDetails';
import axiosInstance from './axiosInstance';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { MdAdd, MdCalendarMonth, MdSubject, MdTaskAlt, MdCheckCircle, MdOutlineToday, MdNoteAdd, MdAddTask, MdClear } from 'react-icons/md';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
import { API_URL } from '../utils/apiConfig';

console.log("Current environment:", process.env.NODE_ENV);
console.log("API_URL", API_URL);

const getPayPeriodsForYear = (year) => {
  const firstPayPeriodStartDate = startOfWeek(new Date(`${year}-01-01`), { weekStartsOn: 0 });
  const payPeriods = [];

  for (let i = 0; i < 52; i++) {
    const startDate = addWeeks(firstPayPeriodStartDate, i);
    const endDate = addDays(startDate, 6);

    payPeriods.push({
      startDate: format(startDate, 'MMM dd, yyyy'),
      endDate: format(endDate, 'MMM dd, yyyy'),
      todos: [],
      notes: [],
      rawStartDate: startDate,
      rawEndDate: endDate,
    });
  }

  return payPeriods;
};

const years = ['2022', '2023', '2024', '2025'];

const PayrollNotes = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentYear = new Date().getFullYear();
  const today = new Date();
  const navigate = useNavigate();
  const { userRole, employeeId, isAuthenticated } = useContext(AuthContext);

  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [payPeriods, setPayPeriods] = useState([]);
  const [showPastPeriods, setShowPastPeriods] = useState(false);
  const [selectedPayPeriodIndex, setSelectedPayPeriodIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 for upcoming, 1 for completed
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  
  // Consolidated data
  const [recentNotes, setRecentNotes] = useState([]);
  const [upcomingTodos, setUpcomingTodos] = useState([]);
  const [completedTodos, setCompletedTodos] = useState([]);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  // Check authentication first
  useEffect(() => {
    if (isAuthenticated === false) {
      console.log('User not authenticated, redirecting to login');
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);
  
  // Add a global API error handler
  useEffect(() => {
    // Create a function to check token validity before making requests
    const tokenCheckInterceptor = axiosInstance.interceptors.request.use(
      config => {
        if (!isAuthenticated) {
          // Cancel the request if not authenticated
          throw new Error('User is not authenticated');
        }
        return config;
      },
      error => Promise.reject(error)
    );
    
    return () => {
      // Clean up interceptor on component unmount
      axiosInstance.interceptors.request.eject(tokenCheckInterceptor);
    };
  }, [isAuthenticated]);

  // Fetch data when employeeId and selectedYear change
  useEffect(() => {
    const fetchData = async () => {
      if (!employeeId) {
        console.log('No employeeId available, skipping data fetch');
        return;
      }

      setLoading(true);
      try {
        console.log('Fetching data for employeeId:', employeeId);
        
        const [payrollNotesResponse, todoItemsResponse] = await Promise.all([
          axiosInstance.get(`${API_URL}/payroll_notes`),
          axiosInstance.get(`${API_URL}/todo_items`),
        ]);

        const payrollNotes = payrollNotesResponse.data;
        const todoItems = todoItemsResponse.data;

        const initialPayPeriods = getPayPeriodsForYear(selectedYear);

        const payPeriodsWithData = initialPayPeriods.map((period) => {
          const start = new Date(period.rawStartDate);
          const end = new Date(period.rawEndDate);

          const associatedNotes = payrollNotes.filter((note) =>
            isWithinInterval(parseISO(note.noteDate), { start, end })
          );

          const associatedTodos = todoItems
            .filter((todo) =>
              isWithinInterval(parseISO(todo.dueDate), { start, end })
            )
            .map((todo) => ({
              ...todo,
              isCompleted: Boolean(todo.isCompleted),
            }));

          return { ...period, notes: associatedNotes, todos: associatedTodos };
        });

        setPayPeriods(payPeriodsWithData);

        // Process data for the summary panel
        const allNotes = payrollNotes.sort((a, b) => 
          new Date(b.createdAt || b.noteDate) - new Date(a.createdAt || a.noteDate)
        );
        
        setRecentNotes(allNotes.slice(0, 5)); // Get 5 most recent notes

        const normalizedTodos = todoItems.map(todo => ({
          ...todo,
          isCompleted: Boolean(todo.isCompleted)
        }));
        
        const upcoming = normalizedTodos
          .filter(todo => !todo.isCompleted && (isFuture(new Date(todo.dueDate)) || isToday(new Date(todo.dueDate))))
          .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
        
        const completed = normalizedTodos
          .filter(todo => todo.isCompleted)
          .sort((a, b) => new Date(b.updatedAt || b.dueDate) - new Date(a.updatedAt || a.dueDate))
          .slice(0, 5); // Get 5 most recently completed
        
        setUpcomingTodos(upcoming);
        setCompletedTodos(completed);

        setError(null);
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          setError('Session expired. Please log in again.');
          navigate('/login');
        } else {
          setError('Failed to load payroll data. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (employeeId) {
      fetchData();
    }
  }, [employeeId, selectedYear, navigate]);

  const handleCloseSnackbar = () => {
    setSnackbar({...snackbar, open: false});
  };

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/login')}
          sx={{ mt: 2 }}
        >
          Return to Login
        </Button>
      </Box>
    );
  }

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    setShowPastPeriods(false);
    setSelectedPayPeriodIndex(null); // Reset selected pay period when year changes
    console.log(`Year changed to: ${year}`);
  };

  const filteredPayPeriods = selectedYear === currentYear.toString() && !showPastPeriods
    ? payPeriods.filter((payPeriod) => new Date(payPeriod.rawEndDate) > today)
    : payPeriods;

  const openPayPeriodModal = (payPeriod) => {
    const index = payPeriods.findIndex(period => 
      period.startDate === payPeriod.startDate && period.endDate === payPeriod.endDate
    );
    if (index !== -1) {
      // Make sure we explicitly include employeeId in the selected period
      setSelectedPayPeriodIndex(index);
      console.log(`Selected PayPeriod Index: ${index}, employeeId: ${employeeId}`);
    }
  };
  
  const closePayPeriodModal = () => {
    setSelectedPayPeriodIndex(null);
    console.log('Closed PayPeriod Modal');
  };

  const openQuickAdd = () => {
    // Find current pay period
    const currentPeriod = payPeriods.find(period => 
      isWithinInterval(today, { 
        start: new Date(period.rawStartDate), 
        end: new Date(period.rawEndDate)
      })
    );
    
    if (currentPeriod) {
      openPayPeriodModal(currentPeriod);
    } else {
      // If can't find current period, use first upcoming period
      const upcomingPeriod = payPeriods.find(period => 
        new Date(period.rawStartDate) > today
      );
      
      if (upcomingPeriod) {
        openPayPeriodModal(upcomingPeriod);
      } else if (payPeriods.length > 0) {
        // Fallback to first period if neither current nor upcoming found
        openPayPeriodModal(payPeriods[0]);
      }
    }
  };

  // Format dates for display
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return dateString;
    }
  };

  // Payroll Notes API Functions
  const createPayrollNote = async (subject, content, noteDate) => {
    if (!employeeId) {
      console.error("No employeeId available:", employeeId);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Employee ID is missing. Please log in again.'
      });
      return;
    }
  
    // Format the date to ensure it's in YYYY-MM-DD format
    const formattedDate = noteDate instanceof Date 
      ? format(noteDate, 'yyyy-MM-dd')
      : noteDate;
  
    console.log("Creating payroll note with data:", {
      employeeId,
      subject,
      content,
      noteDate: formattedDate
    });
  
    const payload = { 
      employeeId, 
      subject, 
      content, 
      noteDate: formattedDate
    };
  
    try {
      const response = await axiosInstance.post(`${API_URL}/payroll_notes`, payload);
      const newNote = response.data;
  

      setPayPeriods((prevPayPeriods) => {
        return prevPayPeriods.map((period) => {
          const periodStart = new Date(period.rawStartDate);
          const periodEnd = new Date(period.rawEndDate);
          const noteDateObj = new Date(newNote.noteDate);

          if (noteDateObj >= periodStart && noteDateObj <= periodEnd) {
            return { ...period, notes: [...period.notes, newNote] };
          }
          return period;
        });
      });
      
      // Add to recent notes
      setRecentNotes(prev => [newNote, ...prev].slice(0, 5));

      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Note created successfully'
      });

      return newNote;
    } catch (error) {
      console.error("Error creating payroll note:", error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Failed to create note. Please try again.'
      });
      throw error;
    }
  };

  const updatePayrollNote = async (noteId, subject, content, noteDate) => {
    if (!employeeId) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Employee ID is missing. Please log in again.'
      });
      return;
    }

    const payload = { employeeId, subject, content, noteDate };
    
    try {
      const response = await axiosInstance.put(`${API_URL}/payroll_notes/${noteId}`, payload);
      const updatedNote = response.data;

      // First remove the note from any period it might be in
      let updatedPayPeriods = payPeriods.map(period => ({
        ...period,
        notes: period.notes.filter(note => note.id !== noteId)
      }));

      // Then add it to the correct period based on new date
      updatedPayPeriods = updatedPayPeriods.map(period => {
        const periodStart = new Date(period.rawStartDate);
        const periodEnd = new Date(period.rawEndDate);
        const noteDateObj = new Date(updatedNote.noteDate);

        if (isWithinInterval(noteDateObj, { start: periodStart, end: periodEnd })) {
          return {
            ...period,
            notes: [...period.notes, updatedNote].sort((a, b) => 
              new Date(b.noteDate) - new Date(a.noteDate))
          };
        }
        return period;
      });

      setPayPeriods(updatedPayPeriods);
      
      // Update recent notes list
      setRecentNotes(prev => {
        const newList = prev.map(note => note.id === noteId ? updatedNote : note);
        return newList.sort((a, b) => new Date(b.createdAt || b.noteDate) - new Date(a.createdAt || a.noteDate));
      });
  
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Note updated successfully'
      });

      return updatedNote;
    } catch (error) {
      console.error("Error updating payroll note:", error.response || error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Failed to update note. Please try again.'
      });
      throw error;
    }
  };

  const deletePayrollNote = async (noteId) => {
    try {
      await axiosInstance.delete(`${API_URL}/payroll_notes/${noteId}`);
  
      setPayPeriods((prevPayPeriods) => {
        return prevPayPeriods.map((period) => ({
          ...period,
          notes: period.notes.filter((note) => note.id !== noteId),
        }));
      });
      
      // Remove from recent notes
      setRecentNotes(prev => prev.filter(note => note.id !== noteId));

      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Note deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting payroll note:', error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Failed to delete note. Please try again.'
      });
    }
  };

  // To-Do Items API Functions
  const createToDoItem = async (description, dueDate = null) => {
    if (!employeeId) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Employee ID is missing. Please log in again.'
      });
      return;
    }

    const payload = {
      employeeId,
      description: description.trim(),
      dueDate: dueDate || null
    };

    try {
      const response = await axiosInstance.post(`${API_URL}/todo_items`, payload);
      const newToDo = { ...response.data, isCompleted: Boolean(response.data.isCompleted) };

      setPayPeriods((prevPayPeriods) => {
        return prevPayPeriods.map((period) => {
          const periodStart = new Date(period.rawStartDate);
          const periodEnd = new Date(period.rawEndDate);
          const dueDateObj = dueDate ? new Date(newToDo.dueDate) : null;

          if (dueDateObj && dueDateObj >= periodStart && dueDateObj <= periodEnd) {
            return { ...period, todos: [...period.todos, newToDo] };
          }
          return period;
        });
      });
      
      // Update upcoming todos list if applicable
      if (!newToDo.isCompleted && (isFuture(new Date(newToDo.dueDate)) || isToday(new Date(newToDo.dueDate)))) {
        setUpcomingTodos(prev => [...prev, newToDo].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)));
      }

      setSnackbar({
        open: true,
        severity: 'success',
        message: 'To-do item created successfully'
      });

      return newToDo;
    } catch (error) {
      console.error("Error creating to-do item:", error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Failed to create to-do item. Please try again.'
      });
      throw error;
    }
  };

  const updateToDoItem = async (todoId, description, isCompleted, dueDate) => {
    if (!employeeId) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Employee ID is missing. Please log in again.'
      });
      return;
    }

    try {
      const response = await axiosInstance.put(`${API_URL}/todo_items/${todoId}`, {
        employeeId,
        description,
        isCompleted,
        dueDate,
      });
      const updatedTodo = response.data;

      // First remove the todo from any period it might be in
      let updatedPayPeriods = payPeriods.map(period => ({
        ...period,
        todos: period.todos.filter(todo => todo.id !== todoId)
      }));

      // Then add it to the correct period based on new date
      updatedPayPeriods = updatedPayPeriods.map(period => {
        const periodStart = new Date(period.rawStartDate);
        const periodEnd = new Date(period.rawEndDate);
        const dueDateObj = dueDate ? new Date(updatedTodo.dueDate) : null;

        if (dueDateObj && isWithinInterval(dueDateObj, { start: periodStart, end: periodEnd })) {
          return {
            ...period,
            todos: [...period.todos, updatedTodo].sort((a, b) => 
              new Date(a.dueDate) - new Date(b.dueDate))
          };
        }
        return period;
      });

      setPayPeriods(updatedPayPeriods);
      
      // Update todo lists
      const todoWithBooleanCompletion = {
        ...updatedTodo,
        isCompleted: Boolean(updatedTodo.isCompleted)
      };
      
      if (todoWithBooleanCompletion.isCompleted) {
        // Remove from upcoming, add to completed
        setUpcomingTodos(prev => prev.filter(todo => todo.id !== todoId));
        setCompletedTodos(prev => [todoWithBooleanCompletion, ...prev].slice(0, 5));
      } else if (isFuture(new Date(todoWithBooleanCompletion.dueDate)) || isToday(new Date(todoWithBooleanCompletion.dueDate))) {
        // Add to upcoming, remove from completed
        setCompletedTodos(prev => prev.filter(todo => todo.id !== todoId));
        setUpcomingTodos(prev => {
          const filtered = prev.filter(todo => todo.id !== todoId);
          return [...filtered, todoWithBooleanCompletion].sort((a, b) => 
            new Date(a.dueDate) - new Date(b.dueDate));
        });
      }

      setSnackbar({
        open: true,
        severity: 'success',
        message: 'To-do item updated successfully'
      });
    } catch (error) {
      console.error('Error updating to-do item:', error);
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        setError('Session expired. Please log in again.');
        navigate('/login');
      } else {
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'Failed to update to-do item. Please try again.'
        });
      }
    }
  };

  const deleteToDoItem = async (todoId) => {
    try {
      await axiosInstance.delete(`${API_URL}/todo_items/${todoId}`);
      console.log(`To-Do Item with ID ${todoId} deleted`);

      setPayPeriods((prevPayPeriods) => {
        const updatedPayPeriods = prevPayPeriods.map((period) => ({
          ...period,
          todos: period.todos.filter((todo) => todo.id !== todoId),
        }));
        return updatedPayPeriods;
      });
      
      // Remove from lists
      setUpcomingTodos(prev => prev.filter(todo => todo.id !== todoId));
      setCompletedTodos(prev => prev.filter(todo => todo.id !== todoId));

      setSnackbar({
        open: true,
        severity: 'success',
        message: 'To-do item deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting to-do item:', error);
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        setError('Session expired. Please log in again.');
        navigate('/login');
      } else {
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'Failed to delete to-do item. Please try again.'
        });
      }
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        width: '100%',
        padding: isMobile ? 1 : 3,
        backgroundColor: '#f9fafb',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: '16px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
          backgroundColor: '#fff',
          mb: 3
        }}
      >
      {/* Header */}
      <Box mb={3} textAlign="center">
        <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom fontWeight="500">
          Payroll Notes
        </Typography>
      </Box>
    
      {/* Year Selection */}
      <Box mb={3}>
        <Grid
          container
          spacing={isMobile ? 1 : 2}
          alignItems="center"
          justifyContent="center"
          padding={isMobile ? 1 : 2}
        >
          <Grid item xs={12} sm="auto" >
            <Typography variant="body1">Select Year:</Typography>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              variant="outlined"
              size="small"
              fullWidth={isMobile}
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm="auto" >
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowPastPeriods(!showPastPeriods)}
              fullWidth={isMobile}
              size="medium"
            >
              {showPastPeriods ? 'Hide Past Periods' : 'Show Past Periods'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    
      <Divider sx={{ marginY: 2, backgroundColor: '#ddd' }} />
    
      {/* Main Content - Split View */}
      <Grid container spacing={2}>
        {/* Left Panel - Pay Periods List */}
        <Grid item xs={12} md={5} lg={4}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 2,
              height: '70vh',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}
          >
            <Typography variant="h6" fontWeight="500" sx={{ mb: 2, flexShrink: 0 }}>
              Pay Periods
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Typography>Loading payroll data...</Typography>
              </Box>
            ) : (
              <Box sx={{ 
                width: '100%', 
                overflow: 'auto',
                flex: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#555',
                  },
                },
              }}>
                {filteredPayPeriods.length > 0 ? (
                  filteredPayPeriods.map((payPeriod, index) => {
                    const isCurrentPeriod = isWithinInterval(new Date(), {
                      start: new Date(payPeriod.rawStartDate),
                      end: new Date(payPeriod.rawEndDate)
                    });
                    
                    return (
                      <Card
                        key={index}
                        sx={{
                          mb: 2,
                          backgroundColor: isCurrentPeriod ? '#e3f2fd' : '#f8fbff',
                          borderRadius: 2,
                          border: isCurrentPeriod ? '1px solid #90caf9' : '1px solid #ddd',
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 3,
                            backgroundColor: isCurrentPeriod ? '#bbdefb' : '#f0f0f0',
                          },
                        }}
                        onClick={() => openPayPeriodModal(payPeriod)}
                      >
                        <CardContent>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Box>
                              <Typography variant="subtitle1" fontWeight={isCurrentPeriod ? "bold" : "normal"}>
                                {payPeriod.startDate} - {payPeriod.endDate}
                              </Typography>
                              {isCurrentPeriod && (
                                <Chip 
                                  label="Current" 
                                  size="small" 
                                  color="primary" 
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {payPeriod.notes.length > 0 && (
                                <Chip 
                                  icon={<MdSubject size={16} />} 
                                  label={payPeriod.notes.length} 
                                  size="small" 
                                  variant="outlined"
                                  color="secondary"
                                />
                              )}
                              {payPeriod.todos.length > 0 && (
                                <Chip 
                                  icon={<MdTaskAlt size={16} />} 
                                  label={payPeriod.todos.length} 
                                  size="small"
                                  variant="outlined" 
                                  color="primary"
                                />
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <Typography textAlign="center" color="textSecondary" p={2}>
                    No pay periods found for the selected criteria.
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* Right Panel - Summary */}
        <Grid item xs={12} md={7} lg={8}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 2,
              height: '70vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" fontWeight="500" sx={{ mb: 2 }}>
              Summary
            </Typography>
            
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab 
                icon={<MdOutlineToday size={20} />}
                iconPosition="start"
                label="Upcoming" 
              />
              <Tab 
                icon={<MdCheckCircle size={20} />}
                iconPosition="start"
                label="Completed" 
              />
            </Tabs>
            
            <Box sx={{ 
              flex: 1, 
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '4px',
                '&:hover': {
                  background: '#555',
                },
              },
            }}>
              {activeTab === 0 ? (
                // Upcoming To-dos
                <Box>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="500">
                      Upcoming To-dos ({upcomingTodos.length})
                    </Typography>
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={openQuickAdd}
                      sx={{ 
                        bgcolor: 'primary.light',
                        '&:hover': { bgcolor: 'primary.main', color: 'white' }
                      }}
                    >
                      <MdNoteAdd size={20} />
                    </IconButton>
                  </Box>
                  
                  {upcomingTodos.length > 0 ? (
                    <List sx={{ pt: 0 }}>
                      {upcomingTodos.map((todo) => (
                        <ListItem 
                          key={todo.id}
                          sx={{
                            backgroundColor: '#f8fbff',
                            mb: 1,
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            '&:hover': {
                              backgroundColor: '#f0f0f0',
                            }
                          }}
                        >
                          <ListItemText
                            primary={todo.description}
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <MdCalendarMonth size={14} />
                                <Typography variant="caption">
                                  Due: {formatDate(todo.dueDate)}
                                </Typography>
                              </Box>
                            }
                          />
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => updateToDoItem(todo.id, todo.description, true, todo.dueDate)}
                          >
                            <MdTaskAlt />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography color="textSecondary" textAlign="center" p={3}>
                      No upcoming to-dos
                    </Typography>
                  )}
                  
                  <Divider sx={{ my: 3 }} />
                  
                  {/* Recent Notes */}
                  <Typography variant="subtitle1" fontWeight="500" sx={{ mb: 2 }}>
                    Recent Notes ({recentNotes.length})
                  </Typography>
                  
                  {recentNotes.length > 0 ? (
                    <List sx={{ pt: 0 }}>
                      {recentNotes.map((note) => (
                        <ListItem 
                          key={note.id}
                          sx={{
                            backgroundColor: '#fffbf0',
                            mb: 1,
                            borderRadius: 1,
                            border: '1px solid #f0d0a0',
                            '&:hover': {
                              backgroundColor: '#fff5e0',
                            }
                          }}
                        >
                          <ListItemText
                            primary={note.subject}
                            secondary={
                              <Box>
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                  {note.content.length > 100 ? `${note.content.substring(0, 100)}...` : note.content}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <MdCalendarMonth size={14} />
                                  <Typography variant="caption">
                                    {formatDate(note.noteDate)}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography color="textSecondary" textAlign="center" p={3}>
                      No notes yet
                    </Typography>
                  )}
                </Box>
              ) : (
                // Completed To-dos
                <Box>
                  <Typography variant="subtitle1" fontWeight="500" sx={{ mb: 2 }}>
                    Recently Completed ({completedTodos.length})
                  </Typography>
                  
                  {completedTodos.length > 0 ? (
                    <List sx={{ pt: 0 }}>
                      {completedTodos.map((todo) => (
                        <ListItem 
                          key={todo.id}
                          sx={{
                            backgroundColor: '#f0fff0',
                            mb: 1,
                            borderRadius: 1,
                            border: '1px solid #c0e0c0',
                            '&:hover': {
                              backgroundColor: '#e8f8e8',
                            }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                {todo.description}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <MdCheckCircle size={14} color="green" />
                                <Typography variant="caption">
                                  Completed on {formatDate(todo.updatedAt || todo.dueDate)}
                                </Typography>
                              </Box>
                            }
                          />
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => updateToDoItem(todo.id, todo.description, false, todo.dueDate)}
                          >
                            <MdClear />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography color="textSecondary" textAlign="center" p={3}>
                      No completed to-dos yet
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    
      {/* Floating Action Button for Mobile */}
      {isMobile && (
        <Fab 
          color="primary" 
          aria-label="add" 
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={openQuickAdd}
        >
          <MdAdd />
        </Fab>
      )}
    
      {/* Pay Period Details Modal - Full Screen */}
      <Dialog
        open={selectedPayPeriodIndex !== null}
        onClose={closePayPeriodModal}
        fullScreen={true} // Always fullscreen
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            margin: 0,
            overflow: 'hidden',
            borderRadius: 0
          }
        }}
      >
        {selectedPayPeriodIndex !== null && payPeriods[selectedPayPeriodIndex] && (
          <PayPeriodDetails
            payPeriod={{ 
              ...payPeriods[selectedPayPeriodIndex],
              employeeId: employeeId // Explicitly pass employeeId here
            }}
            closeModal={closePayPeriodModal}
            createPayrollNote={createPayrollNote}
            updatePayrollNote={updatePayrollNote}
            deletePayrollNote={deletePayrollNote}
            createToDoItem={createToDoItem}
            updateToDoItem={updateToDoItem}
            deleteToDoItem={deleteToDoItem}
          />
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Paper>
    </Container>
  );
};

export default PayrollNotes;